import React, { useState, useRef, useEffect } from 'react';
import './DocSharkPage.css'
import '../../shared/shared.css'
import DocSharkLogo from './DocSharkLogo'
import DocSharkSearchGuide from './DocSharkSearchGuide'
import { performSearch, getSearchCount } from './DocSharkAPI';
import DocSharkResults from './DocSharkResults';
import config from '../../config/config'
import { useSelectedData } from '../../shared/contexts/SelectedContext'
import { useData } from '../../shared/contexts/DataContext';
import { useLocation, useNavigate } from 'react-router-dom';

const DocShark = () => {
  const [isSearchGuideOpen, setIsSearchGuideOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [displayedSearchQuery, setDisplayedSearchQuery] = useState('');
  const [selectedScope, setSelectedScope] = useState('all');
  const [selectedFileTypes, setSelectedFileTypes] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [includeImages, setIncludeImages] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const allData = useData();
  const { selectedData, updateSelection } = useSelectedData()
  const dataPointAmount = selectedData.length;

  // Function to validate search query
  const validateSearchQuery = (query) => {
    // Clear any previous error before validation
    setSearchError(null);

    if (!query || !query.trim()) {
      // An empty or whitespace-only query is handled as not valid for search,
      // but not necessarily an "error" to display if the input is just empty.
      // It will be caught by the !query.trim() checks in handleSearch/executeSearch.
      return false;
    }

    // Rule 1: 1 letter queries are not allowed
    if (query.trim().length === 1) {
      setSearchError("Search queries with only 1 letter are not allowed.");
      return false;
    }

    // Rule 2: Queries consisting only of wildcards (* and ?) and/or spaces are not allowed
    if (/^[\s*?]+$/.test(query)) { // Test against the original query to include spaces
      setSearchError("Search queries consisting only of wildcards (* or ?) and/or spaces are not allowed.");
      return false;
    }

    // Rule 3: ** not allowed
    if (query.includes('**')) {
      setSearchError("Search queries with consecutive wildcards (**) are not allowed.");
      return false;
    }

    // Rule 3: Every * should be connected to 2 a-Z1-9 characters (second neighbor allowed)
    // This implies that for each asterisk, there must be at least two alphanumeric characters
    // within its immediate vicinity (either before, after, or split).
    const alphaNumericRegex = /[a-zA-Z0-9]/;
    const asteriskIndices = [];
    for (let i = 0; i < query.length; i++) {
      if (query[i] === '*') {
        asteriskIndices.push(i);
      }
    }

    for (const index of asteriskIndices) {
      let connectedAlphanumericCount = 0;

      // Check characters before the asterisk
      for (let i = index - 1; i >= 0 && connectedAlphanumericCount < 2; i--) {
        if (alphaNumericRegex.test(query[i])) {
          connectedAlphanumericCount++;
        } else if (query[i] === ' ' || query[i] === '?') {
          // If we encounter a space or a question mark, it breaks the "connected" chain
          // in that direction for counting for THIS asterisk.
          break;
        }
      }

      // Check characters after the asterisk
      for (let i = index + 1; i < query.length && connectedAlphanumericCount < 2; i++) {
        if (alphaNumericRegex.test(query[i])) {
          connectedAlphanumericCount++;
        } else if (query[i] === ' ' || query[i] === '?') {
          // If we encounter a space or a question mark, it breaks the "connected" chain
          // in that direction for counting for THIS asterisk.
          break;
        }
      }

      // If an asterisk doesn't have at least 2 connected alphanumeric characters
      if (connectedAlphanumericCount < 2) {
        setSearchError("Every asterisk (*) must be connected to at least two alphanumeric characters (a-Z, 0-9).");
        return false;
      }
    }

    return true; // Query is valid
  };

  // Check if user wants fresh start (clear=true in URL)
  const shouldClearStorage = new URLSearchParams(location.search).get('clear') === 'true';

  // Parse URL parameters and set initial state
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const queryFromUrl = urlParams.get('q');
    const scopeFromUrl = urlParams.get('scope');
    const fileTypesFromUrl = urlParams.get('fileTypes');
    const includeImagesFromUrl = urlParams.get('includeImages');
    const clearFromUrl = urlParams.get('clear');

    // Clear stored search if requested
    if (clearFromUrl === 'true') {
      try {
        localStorage.removeItem('docshark_last_search');
      } catch (e) {
        console.warn('Could not clear localStorage:', e);
      }
      // Remove clear parameter from URL and return early
      const newParams = new URLSearchParams(location.search);
      newParams.delete('clear');
      const newUrl = newParams.toString() ? `${location.pathname}?${newParams.toString()}` : location.pathname;
      navigate(newUrl, { replace: true });
      return;
    }

    // If no URL parameters, try to load from localStorage
    if (!queryFromUrl && !scopeFromUrl && !fileTypesFromUrl && !includeImagesFromUrl) {
      try {
        const lastSearch = localStorage.getItem('docshark_last_search');
        if (lastSearch) {
          const parsed = JSON.parse(lastSearch);
          if (parsed.query && parsed.query.trim()) {
            setSearchQuery(parsed.query);
            setDisplayedSearchQuery(parsed.query);
            if (parsed.scope) setSelectedScope(parsed.scope);
            if (parsed.fileTypes) setSelectedFileTypes(parsed.fileTypes);
            if (parsed.includeImages) setIncludeImages(parsed.includeImages);

            // Update URL with restored search
            updateUrl(parsed.query, parsed.scope, parsed.fileTypes, parsed.includeImages);
            return;
          }
        }
      } catch (e) {
        console.warn('Could not load last search from localStorage:', e);
      }
    }

    // Set search parameters from URL (normal flow)
    if (queryFromUrl) {
      setSearchQuery(queryFromUrl);
      setDisplayedSearchQuery(queryFromUrl);
    }

    if (scopeFromUrl && ['all', 'current'].includes(scopeFromUrl)) {
      setSelectedScope(scopeFromUrl);
    }

    if (fileTypesFromUrl) {
      const fileTypesArray = fileTypesFromUrl.split(',').filter(type =>
        ['epar', 'annex', 'decision'].includes(type)
      );
      setSelectedFileTypes(fileTypesArray);
    }

    if (includeImagesFromUrl === 'true') {
      setIncludeImages(true);
    }
  }, [location.search]);

  // Auto-execute search when data is available and URL has query
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const queryFromUrl = urlParams.get('q');
    const scopeFromUrl = urlParams.get('scope');
    const fileTypesFromUrl = urlParams.get('fileTypes');
    const includeImagesFromUrl = urlParams.get('includeImages');

    // check if url is valid
    if (!validateSearchQuery(queryFromUrl)) { // Use the validation function here
      return; // Stop further execution of this effect if validation fails
    }

    // Only auto-execute if:
    // 1. There's a query in the URL
    // 2. Required data is loaded (allData and selectedData are available)
    // 3. We haven't already performed a search (searchResults is null)
    // 4. We're not currently loading
    if (queryFromUrl && queryFromUrl.trim() &&
      allData && allData.length > 0 &&
      selectedData &&
      !searchResults &&
      !isLoading) {

      executeSearch(
        queryFromUrl,
        scopeFromUrl || 'all',
        fileTypesFromUrl ? fileTypesFromUrl.split(',').filter(type =>
          ['epar', 'annex', 'decision'].includes(type)
        ) : [],
        includeImagesFromUrl === 'true'
      );
    }
  }, [allData, selectedData, location.search, searchResults, isLoading]);

  // Update URL when search parameters change
  const updateUrl = (query, scope, fileTypes, includeImagesParam) => {
    const params = new URLSearchParams();

    if (query && query.trim()) {
      params.set('q', query);
    }

    if (scope && scope !== 'all') {
      params.set('scope', scope);
    }

    if (fileTypes && fileTypes.length > 0) {
      params.set('fileTypes', fileTypes.join(','));
    }

    if (includeImagesParam) {
      params.set('includeImages', 'true');
    }

    const newUrl = params.toString() ? `${location.pathname}?${params.toString()}` : location.pathname;
    navigate(newUrl, { replace: true });
  };

  const executeSearch = async (query, scope, fileTypes, includeImagesParam) => {
    if (!query || !query.trim()) {
      return;
    }

    // Save search to localStorage
    try {
      const searchData = {
        query,
        scope,
        fileTypes,
        includeImages: includeImagesParam,
        timestamp: Date.now()
      };
      localStorage.setItem('docshark_last_search', JSON.stringify(searchData));
    } catch (e) {
      console.warn('Could not save search to localStorage:', e);
    }

    setIsLoading(true);
    setLoadingStage('Searching...');
    setSearchError(null);

    try {
      const euNumbers = scope === 'current'
        ? selectedData.map(item => item.eu_pnumber.replace(/\//g, '-'))
        : null;

      const fileTypesForSearch = fileTypes && fileTypes.length > 0 ? fileTypes : null;

      const results = await performSearch(
        query,
        euNumbers,
        fileTypesForSearch,
        includeImagesParam,
        setLoadingStage
      );

      setSearchResults(results);
    } catch (error) {
      console.error("Search failed in component:", error);
      setSearchError("Failed to perform search. Please try again.");
    } finally {
      setIsLoading(false);
      setLoadingStage(null);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    if (!validateSearchQuery(searchQuery)) { // Use the validation function here
      return; // Stop the search if validation fails
    }

    setDisplayedSearchQuery(searchQuery);
    updateUrl(searchQuery, selectedScope, selectedFileTypes, includeImages);
    await executeSearch(searchQuery, selectedScope, selectedFileTypes, includeImages);
  };

  const openSearchGuide = () => {
    setIsSearchGuideOpen(true);
  };

  const closeSearchGuide = () => {
    setIsSearchGuideOpen(false);
  };

  const handleStartFresh = () => {
    // Clear all search state
    setSearchQuery('');
    setDisplayedSearchQuery('');
    setSelectedScope('all');
    setSelectedFileTypes([]);
    setIncludeImages(false);
    setSearchResults(null);
    setSearchError(null);

    // Clear localStorage
    try {
      localStorage.removeItem('docshark_last_search');
    } catch (e) {
      console.warn('Could not clear localStorage:', e);
    }

    // Navigate to clean URL
    navigate(location.pathname, { replace: true });
  };

  const handleScopeChange = (e) => {
    const newScope = e.target.value;
    setSelectedScope(newScope);

    // Update URL if there's an active search
    if (searchQuery.trim()) {
      updateUrl(searchQuery, newScope, selectedFileTypes, includeImages);
    }

    // Clear results to indicate parameters changed
    setSearchResults(null);
  };

  const handleFileTypeChange = (e) => {
    const fileType = e.target.value;
    const newFileTypes = e.target.checked
      ? [...selectedFileTypes, fileType]
      : selectedFileTypes.filter(type => type !== fileType);

    setSelectedFileTypes(newFileTypes);

    // Update URL if there's an active search
    if (searchQuery.trim()) {
      updateUrl(searchQuery, selectedScope, newFileTypes, includeImages);
    }

    // Clear results to indicate parameters changed
    setSearchResults(null);
  };

  const handleIncludeImagesChange = (e) => {
    const newIncludeImages = e.target.checked;
    setIncludeImages(newIncludeImages);

    // Update URL if there's an active search
    if (searchQuery.trim()) {
      updateUrl(searchQuery, selectedScope, selectedFileTypes, newIncludeImages);
    }

    // Clear results to indicate parameters changed
    setSearchResults(null);
  };

  return (
    <div className="med-info-content">
      <section className="med-content-container mt-6">
        <div className="flex flex-col items-center justify-center self-center">
          <DocSharkLogo />
          <p className="text-center">Search and explore regulatory documents</p>
          <p className="text-center" style={{color: 'red'}}>Most EPARs NOT included, will be fixed this week - Vincent 11 nov.</p>
          {/* Search container box */}
          <div className="docshark-search-container">
            <div className="form-container-box search-container">
              <input
                type="text"
                id="med-search-input"
                placeholder='"targeted therapy" AND ("breast cancer" OR "ovarian cancer")'
                className="docshark-text-input med-text-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              {/* Clear Button - only show after a search has been performed */}
              {(searchResults || displayedSearchQuery) && (
                <button
                  className="med-primary-solid med-bx-button med-tour-button"
                  onClick={handleStartFresh}
                  title="Clear current search and start fresh"
                >
                  <i className="bx bxs-trash med-button-image"></i>Clear
                </button>
              )}
              <button
                className="med-primary-solid med-bx-button med-search-button"
                onClick={handleSearch}
                disabled={isLoading}
              >
                {isLoading ? (
                  <i className="bx bx-loader-alt bx-spin med-button-image"></i>
                ) : (
                  <i className="bx bx-search med-button-image"></i>
                )}
                {isLoading ? loadingStage : "Search"}
              </button>
              <button
                className="med-primary-solid med-bx-button med-tour-button"
                onClick={openSearchGuide}
              >
                <i className="bx bx-info-circle med-button-image"></i>Guide
              </button>
              {/* Include Images Toggle */}
              <div className="toggle-container">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    name="include-images"
                    checked={includeImages}
                    onChange={handleIncludeImagesChange}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <i
                  className='bx bx-image-alt toggle-icon'
                  onClick={() => setIncludeImages(!includeImages)}>
                </i>
              </div>
            </div>
          </div>

          {/* Scope and File Type Selection box */}
          <div className="selection-box-container mt-6">
            {/* Scope Selection */}
            <div className="form-container-box scope-selection">
              <div className="scope-selection-container">
                <h2 className="text-center">Choose scope:</h2>
                Which medicines to search documents for?
                <hr className="med-top-separator" />
                <div className="scope-options">
                  <label className="scope-option">
                    <input
                      type="radio"
                      name="scope"
                      value="all"
                      checked={selectedScope === 'all'}
                      onChange={handleScopeChange}
                    />
                    <span>All medicines</span>
                  </label>
                  <label className="scope-option">
                    <input
                      type="radio"
                      name="scope"
                      value="current"
                      checked={selectedScope === 'current'}
                      onChange={handleScopeChange}
                      disabled={dataPointAmount === 0}
                    />
                    <span>EMRD selection ({dataPointAmount} {dataPointAmount === 1 ? config.data.singular : config.data.plural})</span>
                  </label>
                </div>
              </div>
            </div>

            {/* File Type Selection */}
            <div className="form-container-box file-type-selection">
              <div className="file-type-selection-container">
                <h2 className="text-center">Select document type(s):</h2>
                Which documents to search through?
                <hr className="med-top-separator" />
                <div className="file-type-options">
                  <label className="file-type-option">
                    <input
                      type="checkbox"
                      name="file-type"
                      value="epar"
                      checked={selectedFileTypes.includes('epar')}
                      onChange={handleFileTypeChange}
                    />
                    <span>Auth. EPAR</span>
                  </label>
                  <label className="file-type-option">
                    <input
                      type="checkbox"
                      name="file-type"
                      value="annex"
                      checked={selectedFileTypes.includes('annex')}
                      onChange={handleFileTypeChange}
                    />
                    <span>EC Auth. Annex (Incl. SmPC)</span>
                  </label>
                  <label className="file-type-option">
                    <input
                      type="checkbox"
                      name="file-type"
                      value="decision"
                      checked={selectedFileTypes.includes('decision')}
                      onChange={handleFileTypeChange}
                    />
                    <span>EC Auth. Decision</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Search Guide Popup */}
            <DocSharkSearchGuide
              isOpen={isSearchGuideOpen}
              onClose={closeSearchGuide}
            />
          </div>

          {/* Search Error Display */}
          {searchError && (
            <div className="med-content-container search-error-container mt-6">
              <div className="search-error-message">
                <i className="bx bx-error-circle"></i>
                <span>{searchError}</span>
              </div>
            </div>
          )}

          {/* Search Results Display */}
          {searchResults && !isLoading && (
            <div className="results-container">
              <DocSharkResults
                searchResults={searchResults}
                searchQuery={searchQuery}
                displayedSearchQuery={displayedSearchQuery}
                allData={allData}
                includeImages={includeImages}
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DocShark;